'use client';

import {
    Accordion,
    AccordionPanel,
    Heading,
    LayoutContainer,
    LinkList,
    LinkListItem,
    TextPassage,
} from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useItems } from '../../utils/useItems';
import {
    parseRichText,
    parseRichContentToPlainText,
} from '../../utils/parseRichText';
import type { FaqProps } from './types';
import { FaqStoreCards } from './FaqStoreCards';
import './Faq.scss';
import { FaqSeo } from '@libs/common-next';

export default function Faq({
    variant,
    headline,
    subHeader,
    lineItem,
}: FaqProps) {
    const faqItems = useItems(lineItem, variant);
    const panelClassnames = cn({ 'faq-question': variant === 'FAQ' });
    const containerClassName = cn('faq', {
        'faq-centered': variant === 'Store Cards',
    });
    return (
        <LayoutContainer className="sparky-u-width-100">
            <div className={containerClassName}>
                <Heading size="title" tagName="h2">
                    {headline}
                </Heading>

                {subHeader && (
                    <TextPassage className="faq-subheader">
                        {subHeader}
                    </TextPassage>
                )}

                <div className="faq-accordions">
                    {faqItems.map(items => (
                        <div
                            className="faq-accordion-group"
                            key={items.at(0)?.sysId}
                        >
                            {items.map(group => (
                                <Accordion key={group.sysId}>
                                    <AccordionPanel
                                        className={panelClassnames}
                                        header={group.question}
                                        data-testid="accordion-panel"
                                    >
                                        <LinkList
                                            className={cn('faq-list', {
                                                'faq-list-cards':
                                                    variant === 'Store Cards',
                                            })}
                                        >
                                            {variant === 'Columns' &&
                                                group.answers.map(a => (
                                                    <LinkListItem
                                                        key={a.sysId}
                                                        text={a.actionText}
                                                        href={a.link.webUrl}
                                                    />
                                                ))}
                                            {variant === 'FAQ' &&
                                                parseRichText(group.answer)}
                                            {variant === 'Store Cards' &&
                                                group?.storeCards && (
                                                    <FaqStoreCards
                                                        key={group.sysId}
                                                        answers={
                                                            group.storeCards
                                                        }
                                                    />
                                                )}
                                        </LinkList>
                                    </AccordionPanel>
                                </Accordion>
                            ))}
                            <FaqSeo
                                id="faq-seo"
                                qaArray={items.map(item => ({
                                    question: item.question,
                                    answer: parseRichContentToPlainText(
                                        item.answer
                                    ),
                                }))}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </LayoutContainer>
    );
}
